import { C, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { Avatar, Button } from '@parsina/framework';

// ─────────────────────────────────────────────────────────────────────────────
// Desktop settings atoms.
//
// The settings page used to redeclare the radius scale, both shadows, a tint
// helper and its own icon set inline. Those are design-system facts, so they
// live in one place now and the screen composes them.
// ─────────────────────────────────────────────────────────────────────────────

// Design-system radius scale (§1.1): controls 10, cards 14.
// There is no hero radius here any more — the page opens with the kit
// `PageHeader` like every other desktop destination (§2.2), not a floating
// gradient card.
export const R_CONTROL = 10;
export const R_CARD = 14;

// The standard card shadow (§1.1) — a black-alpha optical effect, not brand hex.
export const SHADOW_CARD = '0 1px 6px rgba(0,0,0,0.05)';

// LTR island for Latin-digit fields (national id, medical code, phone) — §6.1.
export const LTR = { direction: 'ltr' };

/**
 * A settings section: tinted icon tile, title, subtitle, body.
 *
 * `as` lets a card render as <section> (default) or a plain <div> when it is
 * nested inside one, so the heading outline stays sane (§12.1).
 */
export const SectionCard = ({
  icon, iconBg, iconColor, title, subtitle, testId, actions, children,
}) => (
  <section
    data-testid={testId}
    style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: R_CARD,
      boxShadow: SHADOW_CARD,
      overflow: 'hidden',
    }}
  >
    <header style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '18px 22px 16px', borderBottom: `1px solid ${C.border}`,
    }}>
      <div
        aria-hidden
        style={{
          width: 40, height: 40, borderRadius: R_CONTROL, flexShrink: 0,
          background: iconBg ?? soft(C.navy, 0.09),
          color: iconColor ?? C.navy,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <h2 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 800, color: C.textPrimary }}>{title}</h2>
        {subtitle && (
          <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {actions}
    </header>
    <div style={{ padding: '20px 22px' }}>{children}</div>
  </section>
);

// The widest a run of form fields is allowed to get: two columns of a
// comfortable input. Cards still stretch to the page (§2.2) — this caps the
// CONTENT inside them, which is what stops a wide monitor turning a five-field
// identity form into a single flat row of boxes.
const FIELD_COL = 340;
export const FORM_MAX = FIELD_COL * 2 + 14;

/**
 * Responsive field grid.
 *
 * The old page hardcoded `1fr 1fr`, which forced two columns onto a 360px
 * phone. auto-fit + minmax collapses to one column when a column would fall
 * below `min`, so the same markup serves desktop and the mobile fork's forms
 * without a breakpoint (§4.2).
 *
 * `max` caps the column count. CSS has no "auto-fit but at most N", so the cap
 * is a max-width — but it has to be computed against `min`, not against the
 * column width you have in mind: auto-fit packs as many `min`-wide tracks as
 * fit, so a 694px cap with min 220 still admits THREE columns (3×220 + 2×14 =
 * 688). The binding constraint is therefore "one pixel narrower than N+1 tracks
 * would need"; the comfortable-width cap is applied on top of that, never
 * instead of it.
 */
const maxWidthFor = (max, min, gap) => Math.min(
  FIELD_COL * max + gap * (max - 1),      // don't let a column get too wide
  (max + 1) * min + gap * max - 1,        // and don't let an extra one fit
);

export const FieldGrid = ({ min = 220, max, gap = 14, children }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
    gap,
    ...(max ? { maxWidth: maxWidthFor(max, min, gap) } : null),
  }}>
    {children}
  </div>
);

/**
 * Profile-completeness rail card (§2.2 P2 — the rail beside the profile form).
 *
 * This replaced the completeness *bar* that sat under the identity fields, which
 * in turn replaced the conic dial on the old gradient hero. The bar was honest
 * but inert: it told a doctor they were at 75% and left them to hunt for which
 * field was missing. The rail states the percentage AND lists the gaps, and each
 * gap is a button that focuses its own field — the readout is now the way in.
 *
 * The ring is a stroked SVG, never a conic-gradient: the settings body must
 * paint no gradient at all (`settings.pagestructure.desktop.spec.js` counts
 * them, because a gradient slab is what made this page read as a modal).
 *
 * `role="progressbar"` lives on the ring, so the same assertion that covered the
 * bar still covers this (§12.4: the percentage is written out, never colour-only).
 */
const RING = { size: 96, stroke: 6 };
const RING_R = (RING.size - RING.stroke) / 2;
const RING_C = 2 * Math.PI * RING_R;

export const ProfileStatusCard = ({
  name, specialty, percent, items = [], onJump,
  avatar, avatarBusy, onPickAvatar, onRemoveAvatar,
}) => {
  const { t, formatPercent } = useTranslation();
  const remaining = items.filter((i) => !i.filled);

  // The overlay buttons sit on the ring's inline-start edge, the same corners
  // the clinic app uses on its avatar.
  const overlayBtn = (color) => ({
    position: 'absolute', insetInlineStart: -2,
    width: 26, height: 26, borderRadius: '50%',
    background: C.card, color,
    border: `2px solid ${color}`,
    display: 'grid', placeItems: 'center',
    cursor: avatarBusy ? 'default' : 'pointer',
    boxShadow: SHADOW_CARD, padding: 0,
  });

  return (
    <section
      data-testid="settings-completeness"
      style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: R_CARD, boxShadow: SHADOW_CARD, overflow: 'hidden',
      }}
    >
      <div style={{ padding: '22px 20px 18px', textAlign: 'center' }}>
        <div style={{ position: 'relative', width: RING.size, height: RING.size, margin: '0 auto' }}>
          <svg
            width={RING.size}
            height={RING.size}
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('settings.profile.completeProfile')}
            style={{ transform: 'rotate(-90deg)' }}
          >
            <circle
              cx={RING.size / 2} cy={RING.size / 2} r={RING_R}
              fill="none" stroke={soft(C.navy, 0.12)} strokeWidth={RING.stroke}
            />
            <circle
              cx={RING.size / 2} cy={RING.size / 2} r={RING_R}
              fill="none" stroke={percent === 100 ? C.green : C.teal} strokeWidth={RING.stroke}
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - Math.min(100, Math.max(0, percent)) / 100)}
              style={{ transition: 'stroke-dashoffset .4s ease' }}
            />
          </svg>

          {/* The doctor's picture lives inside the completeness ring — one
              identity object in the rail instead of two stacked circles. The
              percentage stays written out below (§12.4). */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: avatarBusy ? 0.6 : 1, transition: 'opacity 120ms',
          }}>
            <Avatar name={name} size={RING.size - RING.stroke * 2 - 8} src={avatar || undefined} />
          </div>

          {onPickAvatar && (
            <button
              type="button"
              onClick={onPickAvatar}
              disabled={avatarBusy}
              title={avatar ? t('settings.avatar.change') : t('settings.avatar.add')}
              aria-label={avatar ? t('settings.avatar.change') : t('settings.avatar.add')}
              data-testid="settings-avatar-edit"
              style={{ ...overlayBtn(C.navy), bottom: -2 }}
            >
              <Icon.camera s={13} />
            </button>
          )}

          {onRemoveAvatar && avatar && (
            <button
              type="button"
              onClick={onRemoveAvatar}
              disabled={avatarBusy}
              title={t('settings.avatar.removeTitle')}
              aria-label={t('settings.avatar.removeTitle')}
              data-testid="settings-avatar-remove"
              style={{ ...overlayBtn(C.red), top: -2 }}
            >
              <Icon.trash s={13} />
            </button>
          )}
        </div>

        <div dir="auto" style={{ marginTop: 14, fontSize: '0.875rem', fontWeight: 800, color: C.textPrimary }}>
          {name}
        </div>
        {specialty && (
          <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 2 }}>{specialty}</div>
        )}
        <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 6 }}>
          {t('settings.profile.percentComplete', { percent: formatPercent(percent) })}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 16px 16px' }}>
        <div style={{ fontSize: '0.75rem', color: C.textMuted, marginBottom: 10 }}>
          {remaining.length === 0
            ? t('settings.profile.allComplete')
            : t('settings.profile.remaining', { count: remaining.length })}
        </div>

        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item) => (
            <li key={item.key}>
              {item.filled ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 8px', fontSize: '0.78125rem', color: C.textMuted,
                }}>
                  <span style={{ display: 'flex', color: C.green, flexShrink: 0 }}>
                    <Icon.check s={15} />
                  </span>
                  <span style={{ flex: 1, textAlign: 'start' }}>{t(item.labelKey)}</span>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`settings-completeness-jump-${item.key}`}
                  onClick={() => onJump?.(item.key)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
                    background: 'transparent', border: 'none', font: 'inherit',
                    fontSize: '0.78125rem', color: C.textSecondary, textAlign: 'start',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
                      border: `1.5px dashed ${soft(C.navy, 0.35)}`,
                    }}
                  />
                  <span style={{ flex: 1, textAlign: 'start' }}>{t(item.labelKey)}</span>
                  <span style={{ display: 'flex', color: C.textMuted, flexShrink: 0 }}>
                    <Icon.chevron s={14} />
                  </span>
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/**
 * The profile form's commit bar.
 *
 * Sticky to the bottom of the scroll container, because the profile form is now
 * two cards tall beside a rail: editing «نام» at the top of a scrolled page used
 * to push the only way to commit off-screen.
 *
 * This is NOT the framework `SaveBar`. That component is a bare pair of buttons
 * with no dirty message, no in-flight state and no reset — strictly less than
 * this screen needs. Promoting these semantics into the kit is the right move,
 * but it is a framework release (tag + three clients), not a page change.
 */
export const StickySaveBar = ({ saving, onReset, onSave }) => {
  const { t } = useTranslation();
  return (
  <div
    data-testid="settings-savebar"
    role="status"
    style={{
      position: 'sticky', bottom: 0, zIndex: 5,
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      padding: '14px 18px', background: C.card,
      border: `1px solid ${C.border}`, borderRadius: R_CARD,
      boxShadow: '0 -2px 12px rgba(0,0,0,0.07)',
    }}
  >
    <span style={{ display: 'flex', color: C.amber, flexShrink: 0 }}><Icon.alert s={18} /></span>
    <span style={{ flex: 1, minWidth: 160, fontSize: '0.8125rem', color: C.textSecondary }}>
      {t('settings.profile.unsavedShort')}
    </span>
    <Button variant="ghost" onClick={onReset} disabled={saving} data-testid="settings-savebar-reset">
      {t('common.actions.reset')}
    </Button>
    <Button variant="default" onClick={onSave} disabled={saving} data-testid="settings-savebar-submit">
      {saving ? t('common.state.saving') : t('settings.profile.saveInfo')}
    </Button>
  </div>
  );
};

// ── Clinic membership ─────────────────────────────────────────────────────────
//
// The framework `StatusBadge` is not usable here: `statusColors` covers the
// appointment and patient-record vocabularies only, so a membership status
// falls through to its grey default and renders the raw English key.
//
// These are the practitioner service's `clinic_doctors.status` words, verbatim:
// a link is born `pending` and `AcceptClinicDoctor` writes `accepted`. This map
// used to guess `active`/`approved` instead, so every membership the clinic had
// accepted missed it, fell back to `pending` and read «در انتظار تأیید».

const MEMBERSHIP_STATUS = {
  accepted: { labelKey: 'common.state.active',       bg: C.greenLight, color: C.green },
  pending:  { labelKey: 'settings.clinics.pending',  bg: C.amberLight, color: C.amber },
};

// `children` renders under the row, inside the same card — the per-clinic
// settings (today: the تأمین SIAM id) belong to the membership the row shows.
export const ClinicRow = ({ name, meta, status, children }) => {
  const { t } = useTranslation();
  const cfg = MEMBERSHIP_STATUS[status] || MEMBERSHIP_STATUS.pending;
  return (
    <div
      data-testid="settings-clinic-row"
      data-status={status || 'pending'}
      style={{
        padding: '13px 14px', borderRadius: R_CONTROL,
        border: `1px solid ${C.border}`, background: C.card, minWidth: 0,
      }}
    >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <div
        aria-hidden
        style={{
          width: 38, height: 38, borderRadius: R_CONTROL, flexShrink: 0,
          background: soft(C.teal, 0.12), color: C.teal,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon.clinic s={19} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          dir="auto"
          style={{
            fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {name}
        </div>
        {meta && <div style={{ fontSize: '0.71875rem', color: C.textMuted, marginTop: 2 }}>{meta}</div>}
      </div>
      <span style={{
        flexShrink: 0, fontSize: '0.6875rem', fontWeight: 700, borderRadius: 20,
        padding: '3px 10px', background: cfg.bg, color: cfg.color,
      }}>
        {t(cfg.labelKey)}
      </span>
    </div>
    {children}
    </div>
  );
};

/** A labelled group of toggles inside a section. */
export const ToggleGroupBlock = ({ title, hint, style, children }) => (
  <div style={style}>
    {title && (
      <h3 style={{
        margin: '0 0 2px', fontSize: '0.8125rem', fontWeight: 700, color: C.textSecondary,
      }}>
        {title}
      </h3>
    )}
    {hint && (
      <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.7 }}>{hint}</p>
    )}
    <div>{children}</div>
  </div>
);

// Icons — lucide-style inline SVG, the app convention for these tiles.

/**
 * The handoff to حساب پارسینا.
 *
 * ── WHY IT IS NOT JUST A LINK ───────────────────────────────────────────────
 * The settings this replaces were things a doctor came to this tab to DO. A bare
 * «برو به حساب پارسینا» button answers a question they did not ask and leaves
 * them guessing whether what they wanted is even over there. So the card names
 * what is manageable, in the same words the destination uses, before offering to
 * go — the person decides with the list, not after the navigation.
 *
 * It reuses SectionCard rather than inventing a look: the tab still reads as the
 * same settings surface, with one card behaving differently, instead of a
 * foreign panel dropped into it.
 *
 * `note` is for the fact that makes the move make sense — «تا در همهٔ پنل‌ها یکی
 * باشد» — stated once, quietly, and never as an apology.
 */
export const AccountHandoff = ({
  icon, iconBg, iconColor, title, subtitle, items = [], cta, href, note, testId, children,
}) => (
  <SectionCard
    testId={testId}
    icon={icon} iconBg={iconBg} iconColor={iconColor}
    title={title} subtitle={subtitle}
  >
    {children}

    {items.length > 0 && (
      <ul
        style={{
          listStyle: 'none', margin: 0, padding: '14px 16px',
          background: soft(C.navy, 0.04),
          border: `1px solid ${soft(C.navy, 0.10)}`,
          borderRadius: R_CONTROL,
          display: 'flex', flexDirection: 'column', gap: 11,
        }}
      >
        {items.map((label) => (
          <li key={label} style={{ display: 'flex', alignItems: 'center', gap: 10,
            fontSize: '0.84375rem', color: C.textSecondary }}>
            <span
              aria-hidden
              style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: soft(C.teal, 0.16), color: C.teal,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icon.check s={13} />
            </span>
            {label}
          </li>
        ))}
      </ul>
    )}

    <div style={{ display: 'flex', alignItems: 'center', gap: 14,
      flexWrap: 'wrap', marginTop: items.length ? 16 : 0 }}>
      <a
        href={href}
        data-testid={testId ? `${testId}-link` : undefined}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 9,
          height: 42, padding: '0 20px', borderRadius: R_CONTROL,
          background: C.navy, color: '#fff', textDecoration: 'none',
          fontSize: '0.84375rem', fontWeight: 700,
        }}
      >
        {cta}
        {/* Diagonal, so it reads as «opens elsewhere» in both directions
            without needing to be mirrored. */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
      {note && (
        <span style={{ fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.7, maxWidth: 380 }}>
          {note}
        </span>
      )}
    </div>
  </SectionCard>
);

export const Icon = {
  // A three-column page — the visit workspace's shape.
  layout: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v14h3V5H5zm5 0v14h4V5h-4zm6 0v14h3V5h-3z" />
    </svg>
  ),
  user: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  ),
  badge: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" />
    </svg>
  ),
  // A medicine bottle with a cross — the «نسخه» section.
  rx: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M6 3h12v2H6zm11 3H7c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-1 9h-2.5v2.5h-3V15H8v-3h2.5V9.5h3V12H16v3z" />
    </svg>
  ),
  lock: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
    </svg>
  ),
  check: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 16} height={p.s || 16} aria-hidden>
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  ),
  sparkles: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M12 2l1.9 5.1L19 9l-5.1 1.9L12 16l-1.9-5.1L5 9l5.1-1.9L12 2zm6 12l.95 2.55L21.5 17.5l-2.55.95L18 21l-.95-2.55L14.5 17.5l2.55-.95L18 14zM6 14l.95 2.55L9.5 17.5l-2.55.95L6 21l-.95-2.55L2.5 17.5l2.55-.95L6 14z" />
    </svg>
  ),
  palette: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M12 3a9 9 0 000 18c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16a5 5 0 005-5c0-4.42-4.03-8-9-8zm-5.5 9a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3-4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3.5 4a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
    </svg>
  ),
  clinic: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M12 2L2 7v2h20V7L12 2zM4 11v8H2v2h20v-2h-2v-8h-2v8h-3v-8h-2v8h-2v-8H9v8H6v-8H4zm7-1h2v2h2v2h-2v2h-2v-2H9v-2h2v-2z" />
    </svg>
  ),
  alert: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  ),
  camera: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 16} height={p.s || 16} aria-hidden>
      <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
    </svg>
  ),
  trash: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 16} height={p.s || 16} aria-hidden>
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  ),
  globe: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" width={p.s || 20} height={p.s || 20} aria-hidden>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.93 6h-2.95a15.65 15.65 0 00-1.38-3.56A8.03 8.03 0 0118.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 015.08 16zm2.95-8H5.08a7.99 7.99 0 014.33-3.56A15.65 15.65 0 008.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 01-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
    </svg>
  ),
  // Points at the START edge — left in RTL, right in LTR. The glyph is drawn
  // pointing left, so it is mirrored when the page is left-to-right rather than
  // shipped as two paths. `scaleX` is applied by the caller-agnostic transform
  // below so every use site mirrors, not just the ones that remembered to.
  chevron: (p) => (
    <svg
      viewBox="0 0 24 24" fill="currentColor"
      width={p.s || 16} height={p.s || 16} aria-hidden
      style={{ transform: 'scaleX(var(--dir-flip, 1))' }}
    >
      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
    </svg>
  ),
};
